import React, { useState, useEffect } from 'react';
import { X, Receipt, Loader2, Calendar, User, Briefcase, ExternalLink } from 'lucide-react';
import { paymentBatchService } from '../../../services/paymentBatchService';
import { formatCurrency, calculateInstanceFinancials } from '../../../utils/earningsCalculations';

/**
 * PaymentDetailsModal (Contractor View)
 *
 * Modal for viewing payment batch details including:
 * - Proof of payment image uploaded by corporate
 * - Transaction reference
 * - List of all jobs included in the batch with contractor names
 */
export default function PaymentDetailsModal({
  timesheetId,
  onClose
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batch, setBatch] = useState(null);

  useEffect(() => {
    const loadBatchDetails = async () => {
      if (!timesheetId) return;

      setLoading(true);
      setError(null);

      try {
        // Get batch ID for this timesheet
        const batchId = await paymentBatchService.getBatchIdForTimesheet(timesheetId);
        if (batchId) {
          const batchData = await paymentBatchService.getPaymentBatch(batchId);
          setBatch(batchData);
        } else {
          setBatch(null);
        }
      } catch (err) {
        console.error('Error loading payment batch:', err);
        setError(err.message || 'Failed to load payment details');
      } finally {
        setLoading(false);
      }
    };

    loadBatchDetails();
  }, [timesheetId]);

  // Format currency
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2
    }).format(num);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-background w-full max-w-2xl max-h-[90vh] rounded-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Receipt className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">Payment Proof</h2>
              <p className="text-text-secondary text-sm">
                {batch ? `Transaction: ${batch.transaction_ref}` : 'Loading...'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-400 mb-2">{error}</p>
              <button
                onClick={onClose}
                className="text-text-secondary hover:text-text-primary"
              >
                Close
              </button>
            </div>
          ) : !batch ? (
            <div className="text-center py-12">
              <p className="text-text-secondary mb-2">No payment details found.</p>
              <p className="text-text-tertiary text-sm">This payment may have been recorded before the batch payment system was implemented.</p>
            </div>
          ) : (
            <>
              {/* Proof of Payment - Show first for contractor */}
              {batch.proof_url && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-text-secondary mb-3">Proof of Payment</h3>
                  <div className="relative group">
                    <img
                      src={batch.proof_url}
                      alt="Proof of payment"
                      className="w-full max-h-72 object-contain rounded-lg border border-border bg-surface"
                    />
                    <a
                      href={batch.proof_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute top-2 right-2 p-2 bg-black/60 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ExternalLink size={16} />
                    </a>
                  </div>
                </div>
              )}

              {/* Payment Info */}
              <div className="mb-6 p-4 bg-surface rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-text-secondary mb-1">Transaction Reference</p>
                    <p className="text-text-primary font-mono font-medium">{batch.transaction_ref}</p>
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary mb-1">Total Amount</p>
                    <p className="text-text-primary font-bold text-lg">{formatCurrency(batch.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary mb-1">Payment Date</p>
                    <p className="text-text-primary flex items-center gap-1">
                      <Calendar size={14} />
                      {formatDate(batch.created_at)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Jobs in this Batch - The Breakdown */}
              <div>
                <h3 className="text-sm font-medium text-text-secondary mb-3">
                  This payment covers {batch.timesheets?.length || 0} job{batch.timesheets?.length !== 1 ? 's' : ''}
                </h3>
                <div className="space-y-2">
                  {batch.timesheets?.map((timesheet) => {
                    // Calculate earnings using the standard calculation (min 1hr rule)
                    const instanceForCalc = {
                      timesheet: [timesheet],
                      overtime_request: timesheet.job_instance?.overtime_request
                    };
                    const job = timesheet.job_instance?.job;
                    const financials = calculateInstanceFinancials(instanceForCalc, job);
                    const calculatedAmount = (financials.payoutToContractor || 0) +
                      (financials.hasOvertimeApproved ? (financials.overtimePayoutToContractor || 0) : 0);

                    return (
                      <div
                        key={timesheet.id}
                        className="flex items-center justify-between p-3 bg-surface rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                            <Briefcase className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="text-text-primary font-medium">
                              {timesheet.job_instance?.job?.job_name || 'Unknown Job'}
                            </p>
                            <p className="text-text-secondary text-sm flex items-center gap-2">
                              <User size={12} />
                              {timesheet.contractor?.full_name || 'Unknown Contractor'}
                              <span className="text-text-tertiary">•</span>
                              {timesheet.job_instance?.scheduled_date
                                ? new Date(timesheet.job_instance.scheduled_date).toLocaleDateString('en-AU', {
                                    day: 'numeric',
                                    month: 'short'
                                  })
                                : '—'
                              }
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-text-primary font-medium">
                            {formatCurrency(calculatedAmount)}
                          </p>
                        </div>
                      </div>
                    );
                  })}

                  {(!batch.timesheets || batch.timesheets.length === 0) && (
                    <p className="text-text-secondary text-center py-4">
                      No job details available
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-border hover:bg-surface-light text-text-primary rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
